import { cloneDeep } from 'lodash';
import currency from 'currency.js';
import { SituacaoItemCompra } from 'px-business-components';
import { DEFAULT_PRECISION } from '@/utils/produto-utils';
import { converterValorByTaxa } from '@/utils/conversao-moeda-utils';

function isAtivo(produto) {
  return produto.situacao === SituacaoItemCompra.keys.ATIVO.key
    || !produto.situacao
    || !produto.situacaoItemCompra;
}

function total(compra, produtos) {
  if (!produtos.length) {
    return compra.frete.totalTransporte || 0;
  }
  const totalProdutos = produtos
    .filter(produto => isAtivo(produto))
    .map(produto => currency(produto.total, { precision: DEFAULT_PRECISION }).value)
    .reduce((a, b) => currency(a, { precision: DEFAULT_PRECISION })
      .add(currency(b, { precision: DEFAULT_PRECISION })).value, 0) || 0;

  return currency(totalProdutos, { precision: DEFAULT_PRECISION })
    .add(currency(compra.frete.totalTransporte, { precision: DEFAULT_PRECISION }))
    .value || 0;
}

function calculateTotalProdutos(produtos) {
  return produtos
    .filter(produto => isAtivo(produto))
    .map(a => currency(a.totalProdutos, { precision: DEFAULT_PRECISION }).value)
    .reduce((a, b) => currency(a, { precision: DEFAULT_PRECISION })
      .add(currency(b, { precision: DEFAULT_PRECISION })).value, 0);
}

function calculateTotalProdutosComImpostos(produtos) {
  return produtos
    .filter(produto => isAtivo(produto))
    .map(a => a.total)
    .reduce((a, b) => currency(a, { precision: DEFAULT_PRECISION })
      .add(currency(b, { precision: DEFAULT_PRECISION })).value, 0) || 0;
}

function calculateTotalProduto(produto) {
  return currency(produto.quantidade, { precision: DEFAULT_PRECISION })
    .multiply(currency(produto.precoUnitario, { precision: DEFAULT_PRECISION }))
    .value;
}

function calculateIcms(produto) {
  if (!produto.impostos && !produto.impostos?.icms) {
    return 0;
  }
  const { totalProdutos } = produto;
  const icms = currency(produto.impostos.icms, { precision: DEFAULT_PRECISION })
    .multiply(100);
  return icms.multiply(currency(totalProdutos, { precision: DEFAULT_PRECISION }))
    .divide(100).value;
}

function calculateIpi(produto) {
  if (!produto.impostos && !produto.impostos?.ipi) {
    return 0;
  }
  const { totalProdutos } = produto;
  const ipi = currency(produto.impostos.ipi, { precision: DEFAULT_PRECISION }).multiply(100);
  const valorBase = currency(totalProdutos, { precision: DEFAULT_PRECISION })
    .add(currency(produto.frete, { precision: DEFAULT_PRECISION }));

  return ipi.multiply(valorBase).divide(100).value;
}

function calculateTotal(produto) {
  return currency(produto.totalProdutos, { precision: DEFAULT_PRECISION })
    .add(currency(produto.impostos.valorIpi, { precision: DEFAULT_PRECISION }))
    .value;
}

function calculatePercentualRateio(produto, compra, produtos) {
  if (compra.frete.totalTransporte === 0) {
    return 0;
  }
  return currency(produto.totalProdutos, { precision: DEFAULT_PRECISION })
    .multiply(100)
    .divide(currency(calculateTotalProdutos(produtos), { precision: DEFAULT_PRECISION }))
    .value;
}

function calculateFreteItem(produto, compra) {
  if (compra.frete.totalTransporte === 0) {
    return 0;
  }
  return currency(produto.percentualRateio, { precision: DEFAULT_PRECISION })
    .multiply(compra.frete.totalTransporte)
    .divide(100)
    .value;
}

function calculateProdutos(produtos) {
  produtos
    .filter(produto => isAtivo(produto))
    .forEach((produto) => { /* eslint-disable no-param-reassign */
      produto.totalProdutos = calculateTotalProduto(produto);
      produto.impostos.valorIcms = calculateIcms(produto);
    });
}


function calculateRateioIpiTotal(produtos, compra) {
  produtos
    .filter(produto => isAtivo(produto))
    .forEach((produto) => { /* eslint-disable no-param-reassign */
      produto.percentualRateio = calculatePercentualRateio(produto, compra, produtos);
      produto.frete = calculateFreteItem(produto, compra);
      produto.impostos.valorIpi = calculateIpi(produto);
      produto.total = calculateTotal(produto);
    });
}

function calculateSavingForProduto(produto) {
  return currency(produto.$$itemTabelaPreco.saving, { precision: DEFAULT_PRECISION })
    .multiply(produto.quantidade)
    .value;
}

function calculateSavingFromProdutos(produtos) {
  const produtosComTabelaESaving = produtos
    .filter(produto => produto.$$itemTabelaPreco
      && produto.$$itemTabelaPreco.saving
      && isAtivo(produto));

  return produtosComTabelaESaving
    .map(produto => calculateSavingForProduto(produto))
    .reduce((a, b) => currency(a, { precision: DEFAULT_PRECISION })
      .add(currency(b, { precision: DEFAULT_PRECISION })).value, 0);
}

function calcularTotaisCompra(compraParam, produtosParam, taxaVenda) {
  const compra = cloneDeep(compraParam);
  const produtos = cloneDeep(produtosParam);

  if (!compra || !produtos.length) {
    return {
      total: 0,
      totalProdutos: 0,
      totalConvertido: 0,
      totalProdutosConvertido: 0,
    };
  }

  calculateProdutos(produtos);
  calculateRateioIpiTotal(produtos, compra);

  const totalCompra = total(compra, produtos);
  const totalProdutosCompra = calculateTotalProdutosComImpostos(produtos);
  return {
    total: totalCompra,
    totalProdutos: totalProdutosCompra,
    totalConvertido: converterValorByTaxa(totalCompra, taxaVenda),
    totalProdutosConvertido: converterValorByTaxa(totalProdutosCompra, taxaVenda),
  };
}

function calcularTotaisSaving(compra, taxaVenda) {
  if (compra.saving) {
    return {
      saving: compra.saving,
      savingConvertido: converterValorByTaxa(compra.saving, taxaVenda),
    };
  }
  const saving = calculateSavingFromProdutos(compra.produtos);
  return {
    saving,
    savingConvertido: converterValorByTaxa(saving, taxaVenda),
  };
}

function calcularTotaisTransporte(compra, taxaVenda) {
  if (!compra.frete.totalTransporte || compra.frete.totalTransporte === 0) {
    return {
      totalTransporte: 0,
      totalTransporteConvertido: 0,
    };
  }
  return {
    totalTransporte: compra.frete.totalTransporte,
    totalTransporteConvertido: converterValorByTaxa(compra.frete.totalTransporte, taxaVenda),
  };
}

export {
  total,
  calcularTotaisCompra,
  calcularTotaisSaving,
  calcularTotaisTransporte,
};
